import { useEffect, useMemo, useRef } from "react";

import type { BookTimelineLayout } from "@/lib/timeline/book/BookLayout";
import {
  applyChapterTickLabels,
  chapterTickPoses,
  chapterTicks,
  timeJumpTicks,
  timeMarkTicks,
} from "@/lib/timeline/chapter/ChapterTick";

interface ChapterTickLabelsProps {
  canvas: HTMLCanvasElement | null;
  layout: BookTimelineLayout;
  getViewportWidth: () => number;
}

export function ChapterTickLabels({
  canvas,
  layout,
  getViewportWidth,
}: ChapterTickLabelsProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const ticks = useMemo(
    () =>
      layout.journey === "universe"
        ? timeMarkTicks(layout)
        : [...chapterTicks(layout.beats), ...timeJumpTicks(layout)],
    [layout],
  );

  useEffect(() => {
    if (canvas === null || rootRef.current === null) {
      return;
    }
    const root = rootRef.current;
    const sync = (): void => {
      const viewportWidth = getViewportWidth();
      applyChapterTickLabels(
        root,
        chapterTickPoses(ticks, layout.rail.branches, viewportWidth),
        layout,
        viewportWidth,
      );
    };
    canvas.addEventListener("timeline-frame", sync);
    sync();
    return () => canvas.removeEventListener("timeline-frame", sync);
  }, [canvas, getViewportWidth, layout, ticks]);

  if (ticks.length === 0) {
    return null;
  }

  return (
    <div ref={rootRef} className="timeline-chapter-ticks" aria-hidden="true">
      {ticks.map((tick) => (
        <p
          key={tick.chapterId}
          data-chapter-tick={tick.chapterId}
          className={tick.chapterId.startsWith("time-jump-")
            ? "timeline-chapter-tick timeline-chapter-tick--time-jump"
            : "timeline-chapter-tick"}
        >
          {tick.label}
        </p>
      ))}
    </div>
  );
}
